import { useMemo } from 'react';
import { keepPreviousData } from '@tanstack/react-query';
import { Product } from '@wconcept/wconcept-common/models/renewal/product';
import { requestGetPromotionProducts } from '@apis/renewal/promotion';
import { useWckQuery } from './wckQuery/useWckQuery';

const chunkArray = <T>(array: T[], size: number): T[][] => {
  const chunks: T[][] = [];
  for (let i = 0; i < array.length; i += size) {
    chunks.push(array.slice(i, i + size));
  }
  return chunks;
};

interface UsePromotionTabProductsOptions {
  contentId: string;
  tabKey: string;
  itemCodes: string[];
  itemStatusCd?: Array<'01' | '02' | '04' | '05'>;
  productType?: 'GENERAL' | 'SECRET';
  enabled?: boolean;
}

/**
 * 탭 기반 상품 조회 공통 훅
 * - 활성 탭의 상품 코드만 조회하여 불필요한 API 호출 방지
 * - tabKey가 변경되면 새로운 탭의 상품을 조회 (탭 전환 시 자동 fetch)
 * - 이전 탭 데이터는 캐시로 유지되어 재방문 시 재조회 불필요
 */
export function usePromotionTabProducts({
  contentId,
  tabKey,
  itemCodes,
  itemStatusCd,
  productType,
  enabled = true,
}: UsePromotionTabProductsOptions) {
  const uniqueItemCodes = useMemo(
    () => [...new Set(itemCodes)],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [itemCodes.join(',')]
  );

  const { data, isLoading, isSuccess, isError, isFetching } = useWckQuery<
    Product[]
  >({
    queryKey: [
      'promotion-tab-products',
      contentId,
      tabKey,
      productType ?? 'GENERAL',
    ],
    queryFn: async () => {
      if (!uniqueItemCodes.length) return [];
      const batches = chunkArray(uniqueItemCodes, 30);
      const results = await Promise.all(
        batches.map(batch =>
          requestGetPromotionProducts({
            itemCdList: batch,
            itemStatusCdList: itemStatusCd,
            ...(productType !== undefined ? { productType } : {}),
          })
        )
      );
      return results.flatMap(r => r?.productList || []);
    },
    enabled: enabled && uniqueItemCodes.length > 0,
    staleTime: 60 * 1000 * 5,
    gcTime: 60 * 1000 * 10,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  const productsMap = useMemo(() => {
    const map = new Map<string, Product>();
    (data || []).forEach(p => {
      if (p.itemCd) map.set(p.itemCd, p);
    });
    return map;
  }, [data]);

  return {
    products: data || [],
    productsMap,
    isLoading,
    isFetching,
    isSuccess,
    isError,
  };
}
